import React, { useState, useEffect } from "react";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import shopify from "../../../../../js/shopify";

interface MinicartUpgradeProps {
  items: any[];
  settings: {
    upgrade_section_title?: string;
    upgrade_section_title_style?: string;
    upgrade_metafield_key?: string;
    upgrade_button_text?: string;
    upgrade_button_text_style?: string;
    product_title_style?: string;
    add_button_text?: string;
    add_button_text_style?: string;
  };
}

export const MinicartUpgrade = ({ items, settings }: MinicartUpgradeProps) => {
  const [upgradeProducts, setUpgradeProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const titleStyle = settings?.upgrade_section_title_style || 'h5';
  const sectionTitle = settings?.upgrade_section_title || 'Upgrade';
  const metafieldKey = settings?.upgrade_metafield_key || 'bulk_buy_products';
  const buttonText = settings?.upgrade_button_text || 'Upgrade & Save $29.95';
  const productTitleStyle = settings?.product_title_style || 'text-sm font-medium';
  const upgradeButtonTextStyle = settings?.upgrade_button_text_style || 'text-sm text-green-700';
  const addButtonText = settings?.add_button_text || 'Add';
  const addButtonTextStyle = settings?.add_button_text_style || 'text-sm font-medium';
  
  useEffect(() => {
    async function fetchUpgradeProducts() {
      try {
        setLoading(true);
        
        // Get any bulk buy metafields from the cart items
        const productMetafields = [];
        
        items.forEach(item => {
          if (item.product?.metafields) {
            const bulkBuyMetafield = item.product.metafields.find(
              metafield => metafield.key === metafieldKey
            );
            
            if (bulkBuyMetafield && bulkBuyMetafield.value) {
              // Parse the value - it might be a JSON string or comma-separated IDs
              let productIds;
              try {
                productIds = JSON.parse(bulkBuyMetafield.value);
              } catch (e) {
                // If not valid JSON, treat as comma-separated string
                productIds = bulkBuyMetafield.value.split(",").map(id => id.trim());
              }
              
              // Add original product price for comparison
              productIds.forEach(id => {
                productMetafields.push({
                  id,
                  originalPrice: item.price
                });
              });
            }
          }
        });
        
        // If we have product IDs to fetch
        if (productMetafields.length > 0) {
          const productIds = [...new Set(productMetafields.map(product => product.id))];
          const fetchedProducts = await shopify.getShopifyProductList(productIds);
          
          // Filter products that have a higher price than the original item
          const upgrades = fetchedProducts.filter(product => {
            const matchingMetafield = productMetafields.find(meta => meta.id === product.id);
            if (!matchingMetafield) return false;
            
            const productPrice = product.price || product.priceRange?.minVariantPrice?.amount;
            return productPrice > matchingMetafield.originalPrice;
          });
          
          setUpgradeProducts(upgrades.slice(0, 2)); // Limit to 2 products
        }
      } catch (error) {
        console.error("Error fetching upgrade products:", error);
      } finally {
        setLoading(false);
      }
    }
    
    if (items?.length > 0) {
      fetchUpgradeProducts();
    }
  }, [items, metafieldKey]);
  
  if (upgradeProducts.length === 0) {
    return null;
  }
  
  return (
    <div className="minicart-upsells mt-6 mb-4 border-t border-gray-200 pt-4">
      <Paragraph classes={`${titleStyle} mb-4`} text={sectionTitle} />
      
      {upgradeProducts.map(product => (
        <div key={product.id} className="minicart-upsell-product mb-3 p-3 border border-gray-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-16 h-16 flex-shrink-0">
              <img 
                src={product.featured_image || product.images?.[0]?.src} 
                alt={product.title} 
                className="w-full h-full object-cover rounded"
              />
            </div>
            <div className="ml-3">
              <Paragraph classes={productTitleStyle} text={product.title} />
              <Paragraph classes={upgradeButtonTextStyle} text={buttonText} />
            </div>
          </div>
          
          <button 
            className="bg-green-50 text-green-800 hover:bg-green-100 py-2 px-3 rounded-md"
            data-ajax-cart-request-button={`/cart/add?id=${product.variants?.[0]?.id}&quantity=1`}
          >
            <Paragraph classes={addButtonTextStyle} text={addButtonText} />
          </button>
        </div>
      ))}
    </div>
  );
};